import React, { useEffect, useMemo, useState } from 'react';
import { X, ArrowRight, Search, UserPlus, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input, Textarea, Field } from '@/components/ui/Input';
import PhoneField, { isValidPhoneNumber } from '@/components/ui/PhoneField';
import { useToast } from '@/components/ui/Toast';
import { formatINR, pad } from '@/lib/utils';

const todayStr = () => new Date().toISOString().slice(0, 10);

/* Picker that lets admin search + select an existing customer OR quick-create one inline */
export function CustomerPicker({ value, onChange, label = 'Customer' }) {
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [newForm, setNewForm] = useState({ name: '', email: '', phone: '' });
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    api.get('/admin/customers').then((r) => setCustomers(r.data));
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return customers.slice(0, 8);
    return customers.filter((c) => (c.name + ' ' + c.email + ' ' + c.phone).toLowerCase().includes(s)).slice(0, 8);
  }, [q, customers]);

  if (value) {
    return (
      <Field label={label}>
        <div className="flex items-center justify-between border border-volt/40 bg-volt/5 px-4 py-3" data-testid="selected-customer">
          <div>
            <div className="font-medium">{value.name}</div>
            <div className="text-[10px] text-white/40 uppercase tracking-wider">{value.email} · {value.phone || '—'}</div>
          </div>
          <button onClick={() => onChange(null)} className="text-xs uppercase tracking-wider text-white/40 hover:text-volt">Change</button>
        </div>
      </Field>
    );
  }

  if (creating) {
    return (
      <Field label="New customer">
        <div className="space-y-3 border border-white/10 p-4 bg-surface-raised">
          <Input placeholder="Name" value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })} data-testid="picker-new-name" />
          <Input placeholder="Email" type="email" value={newForm.email} onChange={(e) => setNewForm({ ...newForm, email: e.target.value })} data-testid="picker-new-email" />
          <PhoneField value={newForm.phone} onChange={(v) => setNewForm({ ...newForm, phone: v })} defaultCountry="IN" testId="picker-new-phone" />
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setCreating(false)} className="flex-1">Cancel</Button>
            <Button
              size="sm"
              className="flex-1"
              disabled={busy}
              onClick={async () => {
                if (!newForm.name || !newForm.email || !isValidPhoneNumber(newForm.phone || '')) {
                  return toast({ title: 'Fill name, email and a valid phone', variant: 'error' });
                }
                setBusy(true);
                try {
                  const { data } = await api.post('/admin/customers/quick-create', newForm);
                  onChange(data);
                  toast({ title: data.existed ? 'Existing user picked' : 'User created', description: data.temporary_password ? `Temp password: ${data.temporary_password}` : '', variant: 'success' });
                  setCreating(false);
                } catch (e) {
                  toast({ title: 'Create failed', description: e.response?.data?.detail || '', variant: 'error' });
                } finally { setBusy(false); }
              }}
              data-testid="picker-create-user"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <>Create & Pick</>}
            </Button>
          </div>
        </div>
      </Field>
    );
  }

  return (
    <Field label={label}>
      <div className="space-y-2">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / email / phone…" className="pl-10" data-testid="picker-search" />
        </div>
        <div className="max-h-48 overflow-y-auto border border-white/10 bg-surface-raised divide-y divide-white/5">
          {filtered.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onChange(c)}
              className="w-full text-left px-4 py-3 hover:bg-white/5 transition-colors"
              data-testid={`picker-row-${c.id}`}
            >
              <div className="text-sm font-medium">{c.name}</div>
              <div className="text-[10px] text-white/40 uppercase tracking-wider">{c.email} · {c.phone}</div>
            </button>
          ))}
          {filtered.length === 0 && <div className="px-4 py-6 text-center text-white/40 text-sm">No matches.</div>}
        </div>
        <button
          type="button"
          onClick={() => { setCreating(true); setNewForm({ name: '', email: '', phone: '' }); }}
          className="text-xs uppercase tracking-wider text-volt hover:underline inline-flex items-center gap-1"
          data-testid="picker-new-user"
        >
          <UserPlus size={12} /> Or create a new user
        </button>
      </div>
    </Field>
  );
}

export function AdminCreateBookingModal({ onClose, onCreated }) {
  const [customer, setCustomer] = useState(null);
  const [courts, setCourts] = useState([]);
  const [courtId, setCourtId] = useState('');
  const [date, setDate] = useState(todayStr());
  const [startHour, setStartHour] = useState(7);
  const [duration, setDuration] = useState(1);
  const [paymentMode, setPaymentMode] = useState('offline');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [availability, setAvailability] = useState({ slots: [] });
  const { toast } = useToast();

  useEffect(() => {
    api.get('/courts').then((r) => { setCourts(r.data); setCourtId(r.data[0]?.id || ''); });
  }, []);
  useEffect(() => {
    if (!courtId || !date) return;
    api.get(`/courts/${courtId}/availability`, { params: { date } }).then((r) => setAvailability(r.data));
  }, [courtId, date]);

  const court = courts.find((c) => c.id === courtId);
  const total = court ? court.hourly_rate * duration : 0;
  const finalAmount = paymentMode === 'comp' ? 0 : total;

  const submit = async () => {
    if (!customer) return toast({ title: 'Pick a customer', variant: 'error' });
    setBusy(true);
    try {
      await api.post('/admin/bookings/create-on-behalf', {
        customer_id: customer.id, court_id: courtId, booking_date: date,
        start_hour: startHour, duration_hours: duration, payment_mode: paymentMode, note,
      });
      toast({ title: 'Booking created', description: `${court?.name} · ${date}`, variant: 'success' });
      onCreated();
    } catch (e) {
      toast({ title: 'Booking failed', description: e.response?.data?.detail || '', variant: 'error' });
    } finally { setBusy(false); }
  };

  return (
    <ModalShell onClose={onClose} title="New Booking (on behalf)" testId="admin-create-booking-modal">
      <CustomerPicker value={customer} onChange={setCustomer} />
      {customer && (
        <div className="space-y-4 mt-5">
          <Field label="Court">
            <select value={courtId} onChange={(e) => setCourtId(e.target.value)} className="w-full h-11 px-4 bg-transparent border border-white/20 text-white focus:border-volt focus:outline-none" data-testid="abm-court">
              {courts.map((c) => <option key={c.id} value={c.id} className="bg-obsidian">{c.name} · ₹{c.hourly_rate}/hr</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Date">
              <Input type="date" min={todayStr()} value={date} onChange={(e) => setDate(e.target.value)} data-testid="abm-date" />
            </Field>
            <Field label="Start Hour">
              <select value={startHour} onChange={(e) => setStartHour(Number(e.target.value))} className="w-full h-11 px-4 bg-transparent border border-white/20 text-white focus:border-volt focus:outline-none" data-testid="abm-start">
                {availability.slots.map((s) => (
                  <option key={s.hour} value={s.hour} disabled={!s.available} className="bg-obsidian">{pad(s.hour)}:00 {s.available ? '' : `(${s.reason})`}</option>
                ))}
              </select>
            </Field>
            <Field label="Duration"><Input type="number" min="1" max="6" value={duration} onChange={(e) => setDuration(Number(e.target.value))} data-testid="abm-duration" /></Field>
          </div>
          <Field label="Payment mode">
            <div className="flex gap-2">
              {[
                ['offline', 'Paid offline (cash / UPI / card swipe)'],
                ['comp', 'Complimentary (₹0)'],
              ].map(([k, l]) => (
                <button
                  key={k} type="button" onClick={() => setPaymentMode(k)}
                  className={`flex-1 px-3 py-3 text-xs font-bold uppercase tracking-wider border ${paymentMode === k ? 'border-volt text-volt bg-volt/5' : 'border-white/15 text-white/60 hover:border-white/30'}`}
                  data-testid={`abm-mode-${k}`}
                >{l}</button>
              ))}
            </div>
          </Field>
          <Field label="Note (optional)">
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Walk-in customer, paid by UPI…" data-testid="abm-note" />
          </Field>
          <div className="flex items-center justify-between border-t border-white/10 pt-4">
            <div>
              <div className="text-[10px] text-white/40 uppercase tracking-[0.25em]">Charges</div>
              <div className="font-mono text-2xl text-volt">{formatINR(finalAmount)}</div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={submit} disabled={busy} data-testid="abm-submit">{busy ? <Loader2 size={14} className="animate-spin" /> : <>Create Booking <ArrowRight size={14} /></>}</Button>
            </div>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

export function AdminCreateMembershipModal({ onClose, onCreated }) {
  const [customer, setCustomer] = useState(null);
  const [plans, setPlans] = useState([]);
  const [courts, setCourts] = useState([]);
  const [planId, setPlanId] = useState('');
  const [paymentMode, setPaymentMode] = useState('offline');
  const [courtId, setCourtId] = useState('');
  const [startHour, setStartHour] = useState(6);
  const [duration, setDuration] = useState(1);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    api.get('/plans').then((r) => { setPlans(r.data); setPlanId(r.data[0]?.id || ''); });
    api.get('/courts').then((r) => { setCourts(r.data); setCourtId(r.data[0]?.id || ''); });
  }, []);

  const plan = plans.find((p) => p.id === planId);
  const court = courts.find((c) => c.id === courtId);
  const needsSlot = !!plan?.allows_slot_reservation;
  const amount = paymentMode === 'comp' ? 0 : (plan?.price || 0);

  const submit = async () => {
    if (!customer || !plan) return toast({ title: 'Pick a customer and plan', variant: 'error' });
    if (needsSlot && !courtId) return toast({ title: 'Pick a reserved court', variant: 'error' });
    setBusy(true);
    try {
      await api.post('/admin/memberships/create-on-behalf', {
        customer_id: customer.id,
        plan_id: planId,
        payment_mode: paymentMode,
        reserved_court_id: needsSlot ? courtId : null,
        reserved_start_hour: needsSlot ? startHour : null,
        reserved_duration_hours: needsSlot ? duration : null,
        note,
      });
      toast({ title: 'Membership activated', description: `${plan.name} · ${customer.name}`, variant: 'success' });
      onCreated();
    } catch (e) {
      toast({ title: 'Membership failed', description: e.response?.data?.detail || '', variant: 'error' });
    } finally { setBusy(false); }
  };

  return (
    <ModalShell onClose={onClose} title="New Membership (on behalf)" testId="admin-create-membership-modal">
      <CustomerPicker value={customer} onChange={setCustomer} />
      {customer && (
        <div className="space-y-4 mt-5">
          <Field label="Plan">
            <select value={planId} onChange={(e) => setPlanId(e.target.value)} className="w-full h-11 px-4 bg-transparent border border-white/20 text-white focus:border-volt focus:outline-none" data-testid="amm-plan">
              {plans.map((p) => <option key={p.id} value={p.id} className="bg-obsidian">{p.name} · ₹{p.price} · {p.duration_days}d{p.is_team ? ' · Team' : ''}{p.allows_slot_reservation ? ' · Slot lock' : ''}</option>)}
            </select>
          </Field>
          {needsSlot && (
            <div className="border border-volt/30 p-4 bg-volt/5 space-y-3">
              <div className="text-[10px] uppercase tracking-[0.25em] text-volt">Reserved daily slot</div>
              <Field label="Court">
                <select value={courtId} onChange={(e) => setCourtId(e.target.value)} className="w-full h-11 px-4 bg-transparent border border-white/20 text-white focus:border-volt focus:outline-none" data-testid="amm-court">
                  {courts.map((c) => <option key={c.id} value={c.id} className="bg-obsidian">{c.name} · {pad(c.open_hour)}:00–{pad(c.close_hour)}:00</option>)}
                </select>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Start hour">
                  <select value={startHour} onChange={(e) => setStartHour(Number(e.target.value))} className="w-full h-11 px-4 bg-transparent border border-white/20 text-white focus:border-volt focus:outline-none" data-testid="amm-start">
                    {court && Array.from({ length: court.close_hour - court.open_hour }).map((_, i) => {
                      const h = court.open_hour + i;
                      return <option key={h} value={h} className="bg-obsidian">{pad(h)}:00</option>;
                    })}
                  </select>
                </Field>
                <Field label="Duration (hours)">
                  <Input type="number" min="1" max="6" value={duration} onChange={(e) => setDuration(Number(e.target.value))} data-testid="amm-duration" />
                </Field>
              </div>
            </div>
          )}
          <Field label="Payment mode">
            <div className="flex gap-2">
              {[['offline', 'Paid offline'], ['comp', 'Complimentary (₹0)']].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setPaymentMode(k)}
                  className={`flex-1 px-3 py-3 text-xs font-bold uppercase tracking-wider border ${paymentMode === k ? 'border-volt text-volt bg-volt/5' : 'border-white/15 text-white/60 hover:border-white/30'}`}
                  data-testid={`amm-mode-${k}`}>{l}</button>
              ))}
            </div>
          </Field>
          <Field label="Note (optional)">
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="amm-note" />
          </Field>
          <div className="flex items-center justify-between border-t border-white/10 pt-4">
            <div>
              <div className="text-[10px] text-white/40 uppercase tracking-[0.25em]">Charges</div>
              <div className="font-mono text-2xl text-volt">{formatINR(amount)}</div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={submit} disabled={busy} data-testid="amm-submit">{busy ? <Loader2 size={14} className="animate-spin" /> : <>Activate <ArrowRight size={14} /></>}</Button>
            </div>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

function ModalShell({ children, title, onClose, testId }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-surface border border-white/10 max-w-xl w-full p-6 sm:p-8 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={testId}>
        <div className="flex items-start justify-between mb-6 gap-4">
          <h2 className="font-heading font-bold text-2xl uppercase tracking-tight">{title}</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
